import { Link } from "react-router-dom";
import { FadeUp, ScaleIn, StaggerContainer, SlideInLeft, SlideInRight } from "@/components/base/ScrollAnimations";
import { products, companyInfo } from "@/mocks/websiteData";

export default function Products() {
  return (
    <div className="min-h-screen pt-20 md:pt-24">
      {/* Hero */}
      <section className="relative h-[280px] md:h-[380px] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Professional%20solar%20product%20showroom%20with%20solar%20panels%2C%20inverters%2C%20batteries%20and%20street%20lights%20neatly%20arranged%2C%20clean%20modern%20interior%2C%20warm%20bright%20lighting%2C%20Indian%20setting%2C%20professional%20product%20photography&width=1600&height=700&seq=products-showroom-01&orientation=landscape"
          alt="Solar products showroom"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-black/85"></div>
        <div className="relative z-10 section-padding text-center w-full">
          <FadeUp>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent-500/25 backdrop-blur-sm border border-accent-400/30 mb-4">
              <span className="text-sm font-medium text-accent-300">Transparent Pricing · Solar · Drones · EV Chargers · GST Extra</span>
            </div>
          </FadeUp>
          <FadeUp>
            <p className="text-sm font-medium text-background-100/80 mb-2 tracking-wide">NexSunNexova Solar Energy Pvt. Ltd.</p>
          </FadeUp>
          <FadeUp delay={100}>
            <h1 className="font-heading font-bold text-3xl md:text-5xl text-background-50 mb-3">Products & Pricing</h1>
          </FadeUp>
          <FadeUp delay={200}>
            <p className="text-base md:text-lg text-background-200 max-w-2xl mx-auto">Transparent pricing on solar panels, inverters, pumps, batteries, EV chargers, and agricultural drones.</p>
          </FadeUp>
        </div>
      </section>

      {/* Subsidy Banner */}
      <section className="py-6 md:py-8 bg-accent-500">
        <div className="section-padding">
          <ScaleIn>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 text-center">
              <i className="ri-gift-line w-8 h-8 flex items-center justify-center text-2xl text-background-50"></i>
              <div>
                <span className="font-heading font-bold text-lg md:text-xl text-background-50">Government Subsidy Up to {companyInfo.subsidyAmount} Available!</span>
                <p className="text-sm text-accent-100">We handle all paperwork. Eligible under PM Surya Ghar and state schemes.</p>
              </div>
            </div>
          </ScaleIn>
        </div>
      </section>

      {/* Pricing Tables */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="section-padding">
          <FadeUp>
            <div className="text-center mb-10 md:mb-14">
              <span className="text-sm font-semibold text-accent-600 uppercase tracking-wider">Featured Products</span>
              <h2 className="font-heading font-bold text-2xl md:text-4xl text-foreground-900 mt-2">Our Top 3 Product Lines</h2>
              <p className="text-foreground-600 mt-3 max-w-2xl mx-auto">Transparent pricing on our primary sales offerings. All prices are exclusive of GST.</p>
            </div>
          </FadeUp>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 max-w-6xl mx-auto mb-12 md:mb-16">
            {/* EV Chargers Featured */}
            <FadeUp delay={0}>
              <div className="bg-background-100 rounded-2xl border border-background-200/70 overflow-hidden hover:border-secondary-300/50 transition-all hover:-translate-y-1 shadow-sm hover:shadow-md">
                <div className="h-48 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20EV%20charging%20station%20unit%20in%20sleek%20parking%20lot%2C%20multiple%20charger%20posts%20with%20LED%20status%20indicators%2C%20electric%20car%20nearby%2C%20clean%20professional%20product%20photography%2C%20bright%20daylight%2C%20high%20tech%20futuristic%20design&width=600&height=400&seq=product-ev-featured-01&orientation=landscape&nocache=true"
                    alt="EV Charging Stations"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5 md:p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-heading font-semibold text-lg text-foreground-900">EV Charging Stations</h3>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 font-medium">New Arrival</span>
                  </div>
                  <p className="text-sm text-foreground-600 mb-4">AC and DC fast chargers from 3.3 kW home units to 480 kW commercial stations. Prices include GST. Installation, OCPP software, and AMC support available.</p>
                  <div className="overflow-hidden rounded-lg border border-background-200">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-secondary-100">
                          <th className="text-left px-3 py-2 font-semibold text-foreground-700">Model</th>
                          <th className="text-right px-3 py-2 font-semibold text-foreground-700">Price (incl. GST)</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-t border-background-200 hover:bg-background-50 transition-colors">
                          <td className="px-3 py-2 text-foreground-700">3.3 kW AC Home</td>
                          <td className="px-3 py-2 text-right font-semibold text-primary-600">₹19,150</td>
                        </tr>
                        <tr className="border-t border-background-200 hover:bg-background-50 transition-colors">
                          <td className="px-3 py-2 text-foreground-700">7.4 kW AC Wallbox</td>
                          <td className="px-3 py-2 text-right font-semibold text-primary-600">₹40,505</td>
                        </tr>
                        <tr className="border-t border-background-200 hover:bg-background-50 transition-colors">
                          <td className="px-3 py-2 text-foreground-700">12 kW DC Fast</td>
                          <td className="px-3 py-2 text-right font-semibold text-primary-600">₹3,30,500</td>
                        </tr>
                        <tr className="border-t border-background-200 hover:bg-background-50 transition-colors">
                          <td className="px-3 py-2 text-foreground-700">60 kW DC Fast</td>
                          <td className="px-3 py-2 text-right font-semibold text-primary-600">₹11,31,140</td>
                        </tr>
                        <tr className="border-t border-background-200 hover:bg-background-50 transition-colors">
                          <td className="px-3 py-2 text-foreground-700">120 kW DC Fast</td>
                          <td className="px-3 py-2 text-right font-semibold text-primary-600">₹16,48,440</td>
                        </tr>
                        <tr className="border-t border-background-200 hover:bg-background-50 transition-colors">
                          <td className="px-3 py-2 text-foreground-700">180 kW DC Fast</td>
                          <td className="px-3 py-2 text-right font-semibold text-primary-600">₹19,46,640</td>
                        </tr>
                        <tr className="border-t border-background-200 hover:bg-background-50 transition-colors">
                          <td className="px-3 py-2 text-foreground-700">240 kW DC Fast</td>
                          <td className="px-3 py-2 text-right font-semibold text-primary-600">₹22,56,090</td>
                        </tr>
                        <tr className="border-t border-background-200 hover:bg-background-50 transition-colors">
                          <td className="px-3 py-2 text-foreground-700">360 kW DC Fast</td>
                          <td className="px-3 py-2 text-right font-semibold text-primary-600">₹35,42,340</td>
                        </tr>
                        <tr className="border-t border-background-200 hover:bg-background-50 transition-colors">
                          <td className="px-3 py-2 text-foreground-700">480 kW DC Fast</td>
                          <td className="px-3 py-2 text-right font-semibold text-primary-600">₹39,91,740</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </FadeUp>

            {/* Drone Spray Featured */}
            <FadeUp delay={100}>
              <div className="bg-background-100 rounded-2xl border border-background-200/70 overflow-hidden hover:border-accent-300/50 transition-all hover:-translate-y-1 shadow-sm hover:shadow-md">
                <div className="h-48 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20agricultural%20spray%20drone%20on%20ground%20in%20green%20paddy%20field%2C%20folded%20arms%2C%20orange%20and%20black%20design%2C%20spraying%20tank%20visible%2C%20morning%20golden%20light%2C%20professional%20product%20photography%2C%20high%20detail&width=600&height=400&seq=product-drone-featured-01&orientation=landscape&nocache=true"
                    alt="Agricultural Spray Drones"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5 md:p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-heading font-semibold text-lg text-foreground-900">Agricultural Spray Drones</h3>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 font-medium">Farmer's Choice</span>
                  </div>
                  <p className="text-sm text-foreground-600 mb-4">Precision spraying drones with training, spare parts, and AMC packages included.</p>
                  <div className="overflow-hidden rounded-lg border border-background-200">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-accent-100">
                          <th className="text-left px-3 py-2 font-semibold text-foreground-700">Model</th>
                          <th className="text-right px-3 py-2 font-semibold text-foreground-700">Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-t border-background-200 hover:bg-background-50 transition-colors">
                          <td className="px-3 py-2 text-foreground-700">10L Spray Drone</td>
                          <td className="px-3 py-2 text-right font-semibold text-primary-600">₹1,85,000</td>
                        </tr>
                        <tr className="border-t border-background-200 hover:bg-background-50 transition-colors">
                          <td className="px-3 py-2 text-foreground-700">16L Spray Drone</td>
                          <td className="px-3 py-2 text-right font-semibold text-primary-600">₹2,45,000</td>
                        </tr>
                        <tr className="border-t border-background-200 hover:bg-background-50 transition-colors">
                          <td className="px-3 py-2 text-foreground-700">20L Spray Drone</td>
                          <td className="px-3 py-2 text-right font-semibold text-primary-600">₹3,15,000</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </FadeUp>

            {/* Solar EPC Featured */}
            <FadeUp delay={200}>
              <div className="bg-background-100 rounded-2xl border border-background-200/70 overflow-hidden hover:border-primary-300/50 transition-all hover:-translate-y-1 shadow-sm hover:shadow-md">
                <div className="h-48 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20solar%20panel%20installation%20on%20Indian%20residential%20rooftop%2C%20worker%20installing%20panels%2C%20safety%20gear%2C%20clean%20bright%20daylight%2C%20professional%20photography&width=600&height=400&seq=product-epc-featured-01&orientation=landscape&nocache=true"
                    alt="Solar EPC Systems"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5 md:p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-heading font-semibold text-lg text-foreground-900">Solar EPC Systems</h3>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 font-medium">Most Popular</span>
                  </div>
                  <p className="text-sm text-foreground-600 mb-4">Turnkey solar installations from 3 kW to 150 kW. Panels, inverters, mounting, and commissioning included.</p>
                  <div className="overflow-hidden rounded-lg border border-background-200">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-primary-100">
                          <th className="text-left px-3 py-2 font-semibold text-foreground-700">Capacity</th>
                          <th className="text-right px-3 py-2 font-semibold text-foreground-700">Price/kW</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-t border-background-200 hover:bg-background-50 transition-colors">
                          <td className="px-3 py-2 text-foreground-700">3-6 kW (1P)</td>
                          <td className="px-3 py-2 text-right font-semibold text-primary-600">₹66,000</td>
                        </tr>
                        <tr className="border-t border-background-200 hover:bg-background-50 transition-colors">
                          <td className="px-3 py-2 text-foreground-700">10-20 kW</td>
                          <td className="px-3 py-2 text-right font-semibold text-primary-600">₹56,000</td>
                        </tr>
                        <tr className="border-t border-background-200 hover:bg-background-50 transition-colors">
                          <td className="px-3 py-2 text-foreground-700">101-150 kW</td>
                          <td className="px-3 py-2 text-right font-semibold text-primary-600">₹50,000</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </FadeUp>
          </div>

          {/* DJT Haika EV Charger Detailed Price List */}
          <section className="py-10 md:py-14 bg-background-100 rounded-2xl border border-background-200/70 my-10 md:my-14">
            <div className="section-padding">
              <FadeUp>
                <div className="text-center mb-8 md:mb-10">
                  <span className="text-sm font-semibold text-accent-600 uppercase tracking-wider">Official Rate Card</span>
                  <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-900 mt-2">DJT Haika EV Charger Price List</h2>
                  <p className="text-foreground-600 mt-2 max-w-2xl mx-auto text-sm md:text-base">
                    All prices include GST. Breakdown includes machine price, packing charges, and electrical/EHT panel where applicable. Transportation extra as per actuals.
                  </p>
                </div>
              </FadeUp>

              <FadeUp delay={100}>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border border-background-200 rounded-xl overflow-hidden">
                    <thead>
                      <tr className="bg-secondary-500 text-background-50">
                        <th className="text-left px-4 md:px-6 py-3 font-semibold">Power Rating</th>
                        <th className="text-right px-4 md:px-6 py-3 font-semibold">Machine Price<br/><span className="text-xs font-normal opacity-80">(incl. GST @ 5%)</span></th>
                        <th className="text-right px-4 md:px-6 py-3 font-semibold">Packing</th>
                        <th className="text-right px-4 md:px-6 py-3 font-semibold">Electrical / EHT Panel<br/><span className="text-xs font-normal opacity-80">(incl. GST @ 18%)</span></th>
                        <th className="text-right px-4 md:px-6 py-3 font-semibold">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-t border-background-200 bg-background-50 hover:bg-background-100 transition-colors">
                        <td className="px-4 md:px-6 py-3 font-semibold text-foreground-900">3.3 kW AC</td>
                        <td className="px-4 md:px-6 py-3 text-right text-foreground-700">₹18,900</td>
                        <td className="px-4 md:px-6 py-3 text-right text-foreground-700">₹250</td>
                        <td className="px-4 md:px-6 py-3 text-right text-foreground-400">—</td>
                        <td className="px-4 md:px-6 py-3 text-right font-bold text-primary-600">₹19,150</td>
                      </tr>
                      <tr className="border-t border-background-200 hover:bg-background-100 transition-colors">
                        <td className="px-4 md:px-6 py-3 font-semibold text-foreground-900">7.4 kW AC</td>
                        <td className="px-4 md:px-6 py-3 text-right text-foreground-700">₹40,005</td>
                        <td className="px-4 md:px-6 py-3 text-right text-foreground-700">₹500</td>
                        <td className="px-4 md:px-6 py-3 text-right text-foreground-400">—</td>
                        <td className="px-4 md:px-6 py-3 text-right font-bold text-primary-600">₹40,505</td>
                      </tr>
                      <tr className="border-t border-background-200 bg-background-50 hover:bg-background-100 transition-colors">
                        <td className="px-4 md:px-6 py-3 font-semibold text-foreground-900">12 kW DC</td>
                        <td className="px-4 md:px-6 py-3 text-right text-foreground-700">₹3,04,500</td>
                        <td className="px-4 md:px-6 py-3 text-right text-foreground-700">₹6,000</td>
                        <td className="px-4 md:px-6 py-3 text-right text-foreground-700">₹20,000</td>
                        <td className="px-4 md:px-6 py-3 text-right font-bold text-primary-600">₹3,30,500</td>
                      </tr>
                      <tr className="border-t border-background-200 hover:bg-background-100 transition-colors">
                        <td className="px-4 md:px-6 py-3 font-semibold text-foreground-900">60 kW DC</td>
                        <td className="px-4 md:px-6 py-3 text-right text-foreground-700">₹9,97,500</td>
                        <td className="px-4 md:px-6 py-3 text-right text-foreground-700">₹18,000</td>
                        <td className="px-4 md:px-6 py-3 text-right text-foreground-700">₹1,15,640</td>
                        <td className="px-4 md:px-6 py-3 text-right font-bold text-primary-600">₹11,31,140</td>
                      </tr>
                      <tr className="border-t border-background-200 bg-background-50 hover:bg-background-100 transition-colors">
                        <td className="px-4 md:px-6 py-3 font-semibold text-foreground-900">120 kW DC</td>
                        <td className="px-4 md:px-6 py-3 text-right text-foreground-700">₹15,07,800</td>
                        <td className="px-4 md:px-6 py-3 text-right text-foreground-700">₹25,000</td>
                        <td className="px-4 md:px-6 py-3 text-right text-foreground-700">₹1,15,640</td>
                        <td className="px-4 md:px-6 py-3 text-right font-bold text-primary-600">₹16,48,440</td>
                      </tr>
                      <tr className="border-t border-background-200 hover:bg-background-100 transition-colors">
                        <td className="px-4 md:px-6 py-3 font-semibold text-foreground-900">180 kW DC</td>
                        <td className="px-4 md:px-6 py-3 text-right text-foreground-700">₹18,06,000</td>
                        <td className="px-4 md:px-6 py-3 text-right text-foreground-700">₹25,000</td>
                        <td className="px-4 md:px-6 py-3 text-right text-foreground-700">₹1,15,640</td>
                        <td className="px-4 md:px-6 py-3 text-right font-bold text-primary-600">₹19,46,640</td>
                      </tr>
                      <tr className="border-t border-background-200 bg-background-50 hover:bg-background-100 transition-colors">
                        <td className="px-4 md:px-6 py-3 font-semibold text-foreground-900">240 kW DC</td>
                        <td className="px-4 md:px-6 py-3 text-right text-foreground-700">₹20,80,050</td>
                        <td className="px-4 md:px-6 py-3 text-right text-foreground-700">₹25,000</td>
                        <td className="px-4 md:px-6 py-3 text-right text-foreground-700">₹1,51,040</td>
                        <td className="px-4 md:px-6 py-3 text-right font-bold text-primary-600">₹22,56,090</td>
                      </tr>
                      <tr className="border-t border-background-200 hover:bg-background-100 transition-colors">
                        <td className="px-4 md:px-6 py-3 font-semibold text-foreground-900">360 kW DC</td>
                        <td className="px-4 md:px-6 py-3 text-right text-foreground-700">₹33,66,300</td>
                        <td className="px-4 md:px-6 py-3 text-right text-foreground-700">₹25,000</td>
                        <td className="px-4 md:px-6 py-3 text-right text-foreground-700">₹1,51,040</td>
                        <td className="px-4 md:px-6 py-3 text-right font-bold text-primary-600">₹35,42,340</td>
                      </tr>
                      <tr className="border-t border-background-200 bg-background-50 hover:bg-background-100 transition-colors">
                        <td className="px-4 md:px-6 py-3 font-semibold text-foreground-900">480 kW DC</td>
                        <td className="px-4 md:px-6 py-3 text-right text-foreground-700">₹38,15,700</td>
                        <td className="px-4 md:px-6 py-3 text-right text-foreground-700">₹25,000</td>
                        <td className="px-4 md:px-6 py-3 text-right text-foreground-700">₹1,51,040</td>
                        <td className="px-4 md:px-6 py-3 text-right font-bold text-primary-600">₹39,91,740</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </FadeUp>

              <FadeUp delay={200}>
                <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-4 text-sm text-foreground-500">
                  <span className="inline-flex items-center gap-1.5">
                    <i className="ri-truck-line w-4 h-4 flex items-center justify-center text-secondary-500"></i>
                    Transportation charges extra as per actuals
                  </span>
                  <span className="hidden sm:inline text-background-300">|</span>
                  <span className="inline-flex items-center gap-1.5">
                    <i className="ri-shield-check-line w-4 h-4 flex items-center justify-center text-accent-500"></i>
                    All totals include applicable GST
                  </span>
                </div>
              </FadeUp>
            </div>
          </section>

          {/* More Products */}
          <FadeUp>
            <div className="text-center mb-8 md:mb-10">
              <span className="text-sm font-semibold text-accent-600 uppercase tracking-wider">More Products</span>
              <h3 className="font-heading font-bold text-xl md:text-2xl text-foreground-900 mt-2">Additional Solar Products</h3>
            </div>
          </FadeUp>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
            {products.filter(p => !["ev-chargers", "drone-spray", "epc-on-grid"].includes(p.id)).map((product, i) => (
              <FadeUp key={product.id} delay={i * 100}>
                <div className="bg-background-100 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-300/50 transition-all shadow-sm hover:shadow-md">
                  <div className="flex flex-col sm:flex-row">
                    <div className="sm:w-2/5 h-44 sm:h-auto shrink-0 overflow-hidden">
                      <img src={product.image} alt={product.category} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                    </div>
                    <div className="p-5 md:p-6 flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-heading font-semibold text-lg text-foreground-900">{product.category}</h3>
                        {product.badge && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 font-medium">
                            {product.badge}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-foreground-600 mb-4">{product.description}</p>
                      <div className="overflow-hidden rounded-lg border border-background-200">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-secondary-100">
                              <th className="text-left px-3 py-2 font-semibold text-foreground-700">{product.category.includes("Pump") ? "HP" : "Capacity"}</th>
                              <th className="text-right px-3 py-2 font-semibold text-foreground-700">Price</th>
                            </tr>
                          </thead>
                          <tbody>
                            {product.items.slice(0, 4).map((item) => (
                              <tr key={item.name} className="border-t border-background-200 hover:bg-background-50 transition-colors">
                                <td className="px-3 py-2 text-foreground-700">{item.name}</td>
                                <td className="px-3 py-2 text-right font-semibold text-primary-600">{item.price}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Terms */}
      <section className="py-8 md:py-12 bg-background-100 border-t border-background-200">
        <div className="section-padding">
          <SlideInLeft>
            <div className="max-w-3xl mx-auto p-5 md:p-6 rounded-xl bg-background-50 border border-background-200/70">
              <div className="flex items-start gap-3">
                <i className="ri-file-list-3-line w-5 h-5 flex items-center justify-center text-primary-500 mt-0.5 shrink-0"></i>
                <div>
                  <h3 className="font-heading font-semibold text-base text-foreground-900 mb-2">Terms & Conditions</h3>
                  <ul className="space-y-1.5 text-sm text-foreground-600">
                    <li className="flex items-start gap-2">
                      <span className="text-primary-500 mt-1">•</span>
                      <span>GST extra where applicable.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary-500 mt-1">•</span>
                      <span>Prices subject to change based on market rates and availability.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary-500 mt-1">•</span>
                      <span>MOQ (Minimum Order Quantity) as per availability.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary-500 mt-1">•</span>
                      <span>EPC pricing includes installation unless stated otherwise.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary-500 mt-1">•</span>
                      <span>Subsidy amount may vary based on government scheme eligibility and capacity.</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </SlideInLeft>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="section-padding">
          <ScaleIn>
            <div className="max-w-3xl mx-auto text-center p-8 md:p-12 rounded-2xl bg-primary-500 text-background-50 shadow-xl shadow-primary-500/20">
              <h2 className="font-heading font-bold text-2xl md:text-3xl mb-3">Need a Custom Quote?</h2>
              <p className="text-base text-primary-100 mb-6">Every project is unique. Contact us for a site-specific assessment and personalized pricing.</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link to="/contact" className="flex items-center gap-2 px-6 py-3 rounded-full bg-background-50 text-primary-600 font-semibold hover:bg-background-100 transition-all whitespace-nowrap hover:-translate-y-0.5">
                  <i className="ri-mail-send-line w-4 h-4 flex items-center justify-center"></i>
                  Request Quote
                </Link>
                <a href={`tel:${companyInfo.phones[0]}`} className="flex items-center gap-2 px-6 py-3 rounded-full bg-primary-600 text-background-50 font-medium hover:bg-primary-700 transition-all whitespace-nowrap hover:-translate-y-0.5">
                  <i className="ri-phone-line w-4 h-4 flex items-center justify-center"></i>
                  Call {companyInfo.phones[0]}
                </a>
              </div>
            </div>
          </ScaleIn>
        </div>
      </section>
    </div>
  );
}